import { useContext, useEffect, useState } from "react";
import "../Announcement/Announcement.scss";
import { toast } from "react-toastify";
import { confirmDialog } from "primereact/confirmdialog";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import {
  inputStyles,
  labelStyles,
  styles,
} from "../Announcement/AnnouncementStyles.js";
import InputBox from "../../../components/Fields/Input Box/InputBox";
import { AppContext } from "../../../context/AppContext";
import api from "../../../api.js";
import "./StudyMaterials.scss";
import Loader from "../../../components/Loader/Loader.jsx";

const StudyMaterials = () => {
  const { backendUrl } = useContext(AppContext);
  const [allStudyMaterials, setAllStudyMaterials] = useState([]);
  const [studyMaterials, setStudyMaterials] = useState({
    courseName: "",
    programmeType: "",
    documentNames: [""],
    documents: [],
  });
  const [selectedStudyMaterial, setSelectedStudyMaterial] = useState(null);
  const [updatedStudyMaterial, setUpdatedStudyMaterial] = useState({});
  const [visible, setVisible] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  let errorShown = false;
  const [scrollHeight, setScrollHeight] = useState("425px");
  const [formErrors, setFormErrors] = useState({
    courseName: null,
    programmeType: null,
    documentNames: null,
    documents: null,
  });
  const [predefinedCourses, setPredefinedCourses] = useState({
    UG: [],
    PG: [],
  });

  // Programme options
  const programmeOptions = [
    { label: "UG Programmes", value: "UG" },
    { label: "PG Programmes", value: "PG" },
  ];

  // Course data structure
  const COURSES = {
    UG: [
      "Part - 1 Language",
      "Part - 2 Language",
      "B.A. English",
      "B.Sc. Computer Science",
      "B.Sc. Information Technology",
      "B.Sc. Biotechnology",
      "B.Sc. Microbiology",
      "B.Sc. Visual Communication",
      "B.Sc. Hotel Management and Catering Science",
      "B.Sc. Nutrition & Dietetics",
      "B.Sc. Mathematics",
      "B.Sc. Chemistry",
      "Bachelor of Computer Applications (BCA)",
      "B.Com.",
      "Bachelor of Business Administration (BBA)",
      "B.Sc. Artificial Intelligence and Data Science"
    ],
    PG: [
      "M.A. English",
      "M.Sc. Biotechnology",
      "M.Com.",
      "M.Sc. Mathematics",
      "M.Sc. Computer Science",
    ],
  };

  const getCourseOptions = (programmeType) => {
    if (!programmeType) return [];

    const courses = COURSES[programmeType] || [];
    return courses.map((course) => ({
      label: course,
      value: course,
    }));
  };

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerHeight;
      if (screenWidth >= 900) {
        setScrollHeight("650px");
      } else {
        setScrollHeight("440px");
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch predefined courses
  async function fetchPredefinedCourses() {
    try {
      const { data } = await api.get(
        `${backendUrl}/api/auth/getPredefinedCourses`
      );
      if (data.success) {
        setPredefinedCourses(data.courses);
      }
    } catch (error) {
      console.error("Error fetching predefined courses:", error);
    }
  }

  async function fetchAllStudyMaterials() {
    setLoading(true);
    try {
      const { data } = await api.get(
        `${backendUrl}/api/auth/getAllStudyMaterials`
      );
      if (data.success) {
        setAllStudyMaterials(data.materials || []);
      } else {
        toast.error(data.message);
      }
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else if (error.response && error.response.status === 400) {
        toast.error(error.response.data.message);
      } else if (error.response && error.response.status === 404) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAllStudyMaterials();
    fetchPredefinedCourses();
  }, []);

  const validateFormFields = (formData, isEditMode = false) => {
    const errors = {
      courseName: null,
      programmeType: null,
      documentNames: null,
      documents: null,
    };

    if (!formData.courseName || formData.courseName.trim() === "") {
      errors.courseName = "Please select a course";
    } else if (formData.programmeType) {
      // Validate course belongs to selected programme
      const validCourses = COURSES[formData.programmeType] || [];
      if (!validCourses.includes(formData.courseName)) {
        errors.courseName = "Invalid course for selected programme type";
      }
    }

    if (!formData.programmeType || formData.programmeType.trim() === "") {
      errors.programmeType = "Please select programme type";
    }

    if (!isEditMode) {
      // Check if any documents are selected (filter out null values)
      const validDocuments =
        formData.documents?.filter((file) => file !== null) || [];
      if (validDocuments.length === 0) {
        errors.documents = "Please select at least one file to upload";
      }

      // Check document names (filter out empty names)
      const validDocumentNames =
        formData.documentNames?.filter((name) => name && name.trim() !== "") ||
        [];
      if (validDocumentNames.length === 0) {
        errors.documentNames = "Please provide names for all documents";
      } else if (validDocumentNames.length !== validDocuments.length) {
        errors.documentNames = "Please provide names for all selected files";
      }

      // Validate file types and sizes only for non-null files
      if (validDocuments.length > 0) {
        const allowedTypes = [
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/vnd.ms-excel",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.document",
          "text/plain",
          "application/vnd.ms-powerpoint",
          "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        ];

        const maxSize = 10 * 1024 * 1024; // 10MB

        for (let file of validDocuments) {
          if (file && !allowedTypes.includes(file.type)) {
            errors.documents =
              "Only PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, and TXT files are allowed";
            break;
          }
          if (file && file.size > maxSize) {
            errors.documents = "File size must be less than 10MB for each file";
            break;
          }
        }
      }
    }

    return errors;
  };

  const handleAddDocumentField = () => {
    setStudyMaterials((prev) => ({
      ...prev,
      documentNames: [...prev.documentNames, ""],
      documents: [...prev.documents, null],
    }));
  };

  const handleRemoveDocumentField = (index) => {
    if (studyMaterials.documentNames.length === 1) return;

    setStudyMaterials((prev) => ({
      ...prev,
      documentNames: prev.documentNames.filter((_, i) => i !== index),
      documents: prev.documents.filter((_, i) => i !== index),
    }));
  };

  const handleDocumentNameChange = (index, value) => {
    const newDocumentNames = [...studyMaterials.documentNames];
    newDocumentNames[index] = value;
    setStudyMaterials((prev) => ({
      ...prev,
      documentNames: newDocumentNames,
    }));
  };

  const handleFileChange = (index, file) => {
    const newDocuments = [...studyMaterials.documents];
    newDocuments[index] = file;
    setStudyMaterials((prev) => ({
      ...prev,
      documents: newDocuments,
    }));
  };

  async function uploadStudyMaterials(e) {
    e.preventDefault();

    // Filter out empty document names and null files before validation
    const filteredData = {
      ...studyMaterials,
      documentNames: studyMaterials.documentNames.filter(
        (name) => name && name.trim() !== ""
      ),
      documents: studyMaterials.documents.filter((file) => file !== null),
    };

    const errors = validateFormFields(filteredData);
    setFormErrors(errors);

    if (Object.values(errors).some((error) => error !== null)) {
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("courseName", studyMaterials.courseName);
    formData.append("programmeType", studyMaterials.programmeType);

    // Only include non-empty document names and non-null files
    const validDocumentNames = studyMaterials.documentNames.filter(
      (name) => name && name.trim() !== ""
    );
    const validDocuments = studyMaterials.documents.filter(
      (file) => file !== null
    );

    formData.append("documentNames", JSON.stringify(validDocumentNames));

    validDocuments.forEach((file) => {
      formData.append("documents", file);
    });

    try {
      const { data } = await api.post(
        `${backendUrl}/api/auth/uploadStudyMaterials`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (data.success) {
        toast.success(data.message);
        fetchAllStudyMaterials();
        setStudyMaterials({
          courseName: "",
          programmeType: "",
          documentNames: [""],
          documents: [],
        });
        setVisible(false);
        setFormErrors({
          courseName: null,
          programmeType: null,
          documentNames: null,
          documents: null,
        });
      }
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const showConfirmDialog = (rowData) => {
    confirmDialog({
      message: (
        <div className="custom-dialog-content">
          <div className="iconParent">
            <i className="pi pi-times"></i>
          </div>
          <div className="contentParent">
            <h2>Are you sure?</h2>
            <div>
              <p>Do you really want to delete this study material?</p>
              <p>This process cannot be undone.</p>
            </div>
          </div>
        </div>
      ),
      header: "",
      icon: null,
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger custom-delete-btn",
      rejectClassName: "p-button-secondary custom-cancel-btn",
      accept: () => {
        handleDeleteClick(rowData);
      },
    });
  };

  async function handleDeleteClick(rowData) {
    setIsSubmitting(true);
    try {
      const { data } = await api.delete(
        `${backendUrl}/api/auth/deleteStudyMaterial/${rowData.id}`
      );
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
      errorShown = false;
      fetchAllStudyMaterials();
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        toast.error(error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleEditClick = (courseData, document) => {
    setSelectedStudyMaterial({
      ...document,
      courseName: courseData.courseName,
    });
    setUpdatedStudyMaterial({
      documentName: document.documentName,
    });
    setVisible(true);
  };

  const updateStudyMaterial = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { data } = await api.put(
        `${backendUrl}/api/auth/updateStudyMaterial/${selectedStudyMaterial.id}`,
        { documentName: updatedStudyMaterial.documentName }
      );
      if (data.success) {
        toast.success(data.message);
        fetchAllStudyMaterials();
        setVisible(false);
        setSelectedStudyMaterial(null);
        setUpdatedStudyMaterial({});
      }
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadDocument = (doc) => {
    if (!doc?.data || !doc?.documentName || !doc?.contentType) {
      toast.error("Document data is missing");
      return;
    }

    try {
      let base64Data = doc.data;

      if (base64Data.startsWith("data:")) {
        base64Data = base64Data.split(",")[1];
      }

      base64Data = base64Data.replace(/[\r\n]+/g, "").trim();

      const blob = base64ToBlob(base64Data, doc.contentType);

      if (!blob || blob.size === 0) {
        toast.error("File data is empty or corrupted");
        return;
      }

      const url = URL.createObjectURL(blob);
      const fileName =
        doc.originalFilename ||
        `${doc.documentName}.${getFileExtension(doc.contentType)}`;

      const link = window.document.createElement("a"); // ✅ use window.document
      link.href = url;
      link.download = fileName;
      link.style.display = "none";
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Failed to download document. Please try again.");
    }
  };

  const base64ToBlob = (base64, mimeType = "") => {
    const sliceSize = 1024;
    const byteChars = atob(base64);
    const bytesLength = byteChars.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, bytesLength);

      const bytes = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteChars.charCodeAt(offset);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }

    return new Blob(byteArrays, { type: mimeType });
  };

  // Helper function to get file extension
  const getFileExtension = (contentType) => {
    const extensions = {
      "application/pdf": "pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        "docx",
      "application/msword": "doc",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        "xlsx",
      "application/vnd.ms-excel": "xls",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        "pptx",
      "application/vnd.ms-powerpoint": "ppt",
      "text/plain": "txt",
      "image/jpeg": "jpg",
      "image/png": "png",
    };
    return extensions[contentType] || "file";
  };

  return (
    <div className="adminAuditReportsMainContainer adminStudyMaterialsMainContainer">
      <Loader visible={isSubmitting} />

      <div className="headerParent">
        <h2>Welcome to Study Materials Management</h2>
      </div>

      <Dialog
        header={
          selectedStudyMaterial
            ? "Edit Study Material"
            : "Upload Study Materials"
        }
        visible={visible}
        style={{ width: "50vw" }}
        onHide={() => {
          setVisible(false);
          setSelectedStudyMaterial(null);
          setStudyMaterials({
            courseName: "",
            programmeType: "",
            documentNames: [""],
            documents: [],
          });
          setUpdatedStudyMaterial({});
          setIsSubmitting(false);
          setFormErrors({
            courseName: null,
            programmeType: null,
            documentNames: null,
            documents: null,
          });
        }}
        draggable={false}
        resizable={false}
        modal
        className="customAnnouncementDialog"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (selectedStudyMaterial) {
              updateStudyMaterial(e);
            } else {
              uploadStudyMaterials(e);
            }
          }}
        >
          {selectedStudyMaterial ? (
            // Edit Mode
            <>
              <InputBox
                styles={styles}
                inputStyles={inputStyles}
                labelStyles={labelStyles}
                fieldName="documentName"
                customLabelName="Document Name"
                placeholder="Enter document display name"
                value={updatedStudyMaterial.documentName || ""}
                handleChange={(e) => {
                  const val = e.target.value;
                  setUpdatedStudyMaterial((prev) => ({
                    ...prev,
                    documentName: val,
                  }));
                }}
              />
              <div
                style={{
                  margin: "8px 0px",
                  fontFamily: "var(--font-paragraph)",
                  fontSize: "var(--paragraph-font-size)",
                  padding: "8px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "4px",
                }}
              >
                <strong>Course:</strong> {selectedStudyMaterial.courseName}
                <br />
                <strong>Current File:</strong>{" "}
                {selectedStudyMaterial.originalFilename}
              </div>
            </>
          ) : (
            // Upload Mode
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <div
                  className="inputFieldWrapper"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    width: "49%",
                  }}
                >
                  <label style={labelStyles}>Programme Type</label>
                  <Dropdown
                    value={studyMaterials.programmeType}
                    onChange={(e) => {
                      setStudyMaterials((prev) => ({
                        ...prev,
                        programmeType: e.value,
                        courseName: "", // Reset course when programme changes
                      }));
                      setFormErrors((prev) => ({
                        ...prev,
                        programmeType: null,
                        courseName: null,
                      }));
                    }}
                    options={programmeOptions}
                    placeholder="Select Programme Type"
                    style={inputStyles}
                    className="dropdown_fields"
                  />
                  {formErrors.programmeType && (
                    <div className="validation-error">
                      <i className="pi pi-exclamation-triangle"></i>
                      {formErrors.programmeType}
                    </div>
                  )}
                </div>

                <div
                  className="inputFieldWrapper"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    width: "49%",
                  }}
                >
                  <label style={labelStyles}>Course Name</label>
                  <Dropdown
                    value={studyMaterials.courseName}
                    onChange={(e) => {
                      setStudyMaterials((prev) => ({
                        ...prev,
                        courseName: e.value,
                      }));
                      setFormErrors((prev) => ({ ...prev, courseName: null }));
                    }}
                    options={getCourseOptions(studyMaterials.programmeType)}
                    placeholder={
                      studyMaterials.programmeType
                        ? "Select Course"
                        : "Select Programme Type First"
                    }
                    disabled={!studyMaterials.programmeType}
                    style={inputStyles}
                    className="dropdown_fields"
                  />
                  {formErrors.courseName && (
                    <div className="validation-error">
                      <i className="pi pi-exclamation-triangle"></i>
                      {formErrors.courseName}
                    </div>
                  )}
                </div>
              </div>

              <div className="documents-section">
                <label style={labelStyles}>Documents</label>

                {studyMaterials.documentNames.map((_, index) => (
                  <div key={index} className="document-row">
                    <div className="document-input-group">
                      <InputBox
                        styles={{ marginBottom: "8px" }}
                        inputStyles={inputStyles}
                        labelStyles={{ ...labelStyles, fontSize: "17px" }}
                        fieldName={`documentName-${index}`}
                        customLabelName={`Document ${index + 1} Name`}
                        placeholder="Enter document name"
                        value={studyMaterials.documentNames[index] || ""}
                        handleChange={(e) =>
                          handleDocumentNameChange(index, e.target.value)
                        }
                      />

                      <div className="file-input-group">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                          style={inputStyles}
                          onChange={(e) =>
                            handleFileChange(index, e.target.files[0])
                          }
                        />
                        {studyMaterials.documentNames.length > 1 && (
                          <Button
                            icon="pi pi-times"
                            className="p-button-danger p-button-text"
                            onClick={() => handleRemoveDocumentField(index)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {formErrors.documentNames && (
                  <div className="validation-error">
                    <i className="pi pi-exclamation-triangle"></i>
                    {formErrors.documentNames}
                  </div>
                )}

                {formErrors.documents && (
                  <div className="validation-error">
                    <i className="pi pi-exclamation-triangle"></i>
                    {formErrors.documents}
                  </div>
                )}

                <div style={{ margin: "5px 0 20px 0" }}>
                  <Button
                    type="button"
                    icon="pi pi-plus"
                    label="Add Another Document"
                    className="p-button-text"
                    onClick={handleAddDocumentField}
                    style={{ marginTop: "10px" }}
                  />
                </div>
              </div>
            </>
          )}

          <div className="submitBtnParent">
            <button
              type="submit"
              className="submitButton"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="pi pi-spinner pi-spin"></i> Processing...
                </>
              ) : selectedStudyMaterial ? (
                "Update Document"
              ) : (
                "Upload Materials"
              )}
            </button>
          </div>
        </form>
      </Dialog>

      <div className="uploadBtnsParent">
        <Button
          label="Upload Study Materials"
          onClick={() => setVisible(true)}
          className="uploadBtn"
        />
      </div>

      <article className="tableWrapper">
        <div className="scrollableTableContainer">
          <DataTable
            value={allStudyMaterials}
            showGridlines
            stripedRows
            className="table"
            emptyMessage="No study materials available"
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 20]}
            scrollable
            scrollHeight={scrollHeight}
            loading={loading}
          >
            <Column
              header="S.No"
              body={(_, options) => options.rowIndex + 1}
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
            />
            <Column
              field="courseName"
              header="Course Name"
              className="tableData"
              style={{ minWidth: "250px" }}
              bodyStyle={{ whiteSpace: "normal" }}
            />
            <Column
              field="programmeType"
              header="Programme"
              className="tableData"
              style={{ minWidth: "120px" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
            />
            <Column
              header="Documents"
              className="tableData"
              style={{ minWidth: "300px" }}
              bodyStyle={{ whiteSpace: "normal" }}
              body={(rowData) => (
                <div>
                  {rowData.documents?.map((doc, index) => (
                    <div key={doc.id} className="document-item">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                        }}
                      >
                        <span>{doc.documentName}</span>
                        <div>
                          <div className="custom-tooltip-wrapper download">
                            <Button
                              icon="pi pi-download"
                              rounded
                              text
                              severity="info"
                              onClick={() => downloadDocument(doc)}
                            />
                            <span className="custom-tooltip-name">
                              Download
                            </span>
                          </div>

                          <div className="custom-tooltip-wrapper edit">
                            <Button
                              icon="pi pi-pencil"
                              rounded
                              text
                              severity="warning"
                              onClick={() => handleEditClick(rowData, doc)}
                            />
                            <span className="custom-tooltip-name">Edit</span>
                          </div>

                          <div className="custom-tooltip-wrapper delete">
                            <Button
                              icon="pi pi-trash"
                              rounded
                              text
                              severity="danger"
                              onClick={() => showConfirmDialog(doc)}
                            />
                            <span className="custom-tooltip-name">Delete</span>
                          </div>
                        </div>
                      </div>

                      {index < rowData.documents.length - 1 && (
                        <hr style={{ margin: "5px 0" }} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            />
          </DataTable>
        </div>
      </article>
    </div>
  );
};

export default StudyMaterials;
